import { LegacyPortalAccountService } from "@spwn-portal/grpc";
import { useMemo } from "react";
import useSWRImmutable from "swr/immutable";
import { createCrossOriginPrivateConnectClient } from "utility/connectWeb";

import { startggConfig } from "./startggConfig";
import { useStartggLink } from "./useStartggLink";

import type { StartggLink } from "./useStartggLink";

/**
 * 連携とエントリーを合わせた、購入条件の充足状態。
 * entered 以外はチケットをカートに入れられない。
 */
export type StartggEntryStatus =
  | "not-linked"
  | "checking"
  | "entered"
  | "not-entered"
  | "failed";

/**
 * エントリー状況は start.gg 側のキャッシュで最大1分ほど遅れて反映される。
 * 自動で追いかけるとレート制限に当たるので、再取得はユーザーの操作でのみ行う。
 */
export const useStartggEntry = (): {
  status: StartggEntryStatus;
  link: StartggLink | null;
  save: (next: StartggLink) => void;
  clear: () => void;
  recheck: () => void;
  isChecking: boolean;
} => {
  const { link, save, clear } = useStartggLink();
  const client = useMemo(
    () => createCrossOriginPrivateConnectClient(LegacyPortalAccountService),
    []
  );

  const entryStatus = useSWRImmutable(
    link
      ? ([
          "startggEntryStatus",
          link.userId,
          startggConfig.tournamentId,
          startggConfig.eventId,
        ] as const)
      : null,
    async () =>
      client.getStartggEntryStatus({
        startggUserId: link?.userId ?? "",
        startggGamerTag: link?.username ?? "",
        tournamentId: startggConfig.tournamentId,
        eventId: startggConfig.eventId,
      })
  );

  const status = ((): StartggEntryStatus => {
    if (!link) {
      return "not-linked";
    }
    if (entryStatus.data !== undefined) {
      return entryStatus.data.entered ? "entered" : "not-entered";
    }
    return entryStatus.error === undefined ? "checking" : "failed";
  })();

  return {
    status,
    link,
    save,
    clear,
    recheck: () => void entryStatus.mutate(),
    isChecking: entryStatus.isValidating,
  };
};
